/**
 * SpacingSlider - Slider control for module/row spacing
 *
 * Reusable slider with label and value display.
 * Supports custom value formatting for different units (cm, m, multiplier).
 */

import React from 'react';

interface SpacingSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  onChangeEnd?: () => void;
  /** Custom format function for display value. Defaults to showing value with unit. */
  formatValue?: (value: number) => string;
  /** Label style: 'semibold' for main panels, 'medium' for segment editing */
  labelStyle?: 'semibold' | 'medium';
  className?: string;
}

export function SpacingSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  onChangeEnd,
  formatValue,
  labelStyle = 'medium',
  className = '',
}: SpacingSliderProps) {
  const displayValue = formatValue ? formatValue(value) : value.toFixed(2);
  const labelClass = labelStyle === 'semibold'
    ? 'block font-semibold text-gray-700 mb-1'
    : 'block text-xs font-medium text-gray-700 mb-1';

  return (
    <div className={className}>
      <label className={labelClass}>
        {label}: {displayValue}
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onMouseUp={onChangeEnd}
        className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer"
      />
    </div>
  );
}

export default SpacingSlider;
